import styled from "styled-components";
import React, { ChangeEvent, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { signIn } from "@utils/muiSignIn";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

interface SignInPageProps {
  redirectUrl?: string;
}

const Sign = ({ redirectUrl }: SignInPageProps) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [formData, setFormData] = useState({
    login: "",
    password: "",
  });

  useEffect(() => {
    const { login, password } = formData;

    const sendButtonDisabled = login.length < 3 || password.length < 6;

    if (sendButtonDisabled) setDisabled(true);
    else setDisabled(false);
  }, [formData]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const onFormValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const onFormDataSend = async () => {
    try {
      setLoading(true);

      await signIn(formData, redirectUrl);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const slotProps = {
    htmlInput: {
      sx: { paddingTop: "12px", paddingBottom: "12px" },
    },
    inputLabel: {
      sx: { lineHeight: "1rem" },
    },
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <Avatar sx={{ my: 1, mb: 2, bgcolor: "primary.main" }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography variant="h5" color="textPrimary" gutterBottom>
          Sign in
        </Typography>

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Welcome user, please sign in to continue.
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ position: "relative" }}>
            <TextField
              margin="dense"
              required
              slotProps={slotProps}
              fullWidth
              id="login"
              label="Login"
              name="login"
              type="text"
              onChange={onFormValueChange}
              placeholder="Login"
            />

            <PassWrapper>
              <TextField
                margin="dense"
                required
                fullWidth
                slotProps={slotProps}
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="Password"
                onChange={onFormValueChange}
                autoComplete="current-password"
              />

              {showPassword ? (
                <VisiblePasswordIcon onClick={handleClickShowPassword} />
              ) : (
                <InVisiblePasswordIcon onClick={handleClickShowPassword} />
              )}
            </PassWrapper>

            <LoadingButton
              type="submit"
              fullWidth
              size="large"
              variant="contained"
              disableElevation
              color={"primary"}
              loading={loading}
              disabled={disabled}
              onClick={onFormDataSend}
              sx={{
                mt: 3,
                mb: 2,
                textTransform: "capitalize",
                filter: "opacity(0.9)",
                transition: "filter 0.2s ease-in",
                "&:hover": {
                  filter: "opacity(1)",
                },
              }}
            >
              Sign In
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

const VisiblePasswordIcon = styled(Visibility)`
  position: absolute;
  top: 18px;
  right: 10px;

  cursor: pointer;
  z-index: 1;
`;

const InVisiblePasswordIcon = styled(VisibilityOff)`
  position: absolute;
  top: 18px;
  right: 10px;

  cursor: pointer;
  z-index: 1;
`;

const PassWrapper = styled.span`
  position: relative;
`;

export default Sign;
